import { useState, useRef, useEffect, useCallback } from 'react';
import { FloraAIIcon } from '@practium/ui';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import styles from './ExpandableFloraCard.module.css';

interface ExpandableFloraCardProps {
	onFloraClick: () => void;
	onSendMessage?: (message: string) => void;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
}

export const ExpandableFloraCard = ({
	onFloraClick,
	onSendMessage,
	isExpanded = false,
	onToggleExpand,
}: ExpandableFloraCardProps) => {
	const [message, setMessage] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
		};
	}, []);

	const handleToggle = useCallback(() => {
		onToggleExpand?.();
		if (!isExpanded) {
			focusTimerRef.current = setTimeout(() => inputRef.current?.focus(), 300);
		}
	}, [isExpanded, onToggleExpand]);

	const handleSend = () => {
		if (!message.trim()) return;
		if (onSendMessage) onSendMessage(message.trim());
		else onFloraClick();
		setMessage('');
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className={`${styles.card} ${isExpanded ? styles.expanded : ''}`}>
			{!isExpanded && (
				<div className={styles.collapsedHeader} onClick={handleToggle}>
					<div className={styles.collapsedContent}>
						<h3 className={styles.collapsedTitle}>Difícil Preencher?</h3>
						<p className={styles.collapsedSubtitle}>
							Chame a Flora AI pra te ajudar a preencher os seus parcelamentos{' '}
						</p>
					</div>
					<div className={styles.iconContainer}>
						<FloraAIIcon className={styles.icon} />
					</div>
				</div>
			)}

			{isExpanded && (
				<>
					<div className={styles.expandedHeader} onClick={handleToggle}>
						<div className={styles.expandedHeaderLeft}>
							<div className={styles.floraIconSmall}>
								<FloraAIIcon className={styles.floraIconSmallImg} />
							</div>
							<span className={styles.expandedHeaderTitle}>Flora AI</span>
						</div>
						<KeyboardArrowDown className={styles.chevron} />
					</div>

					<div className={styles.expandedBody}>
						<FloraAIIcon className={styles.floraIconLarge} />
						<h4 className={styles.bodyTitle}>Parcelamento</h4>
						<p className={styles.bodyDescription}>
							Estou aqui para te ajudar com o parcelamento, desde responder perguntas até dar
							recomendações. Vamos conversar!
						</p>
						<p className={styles.bodyExample}>
							Exemplo: Qual é o valor de cálcio recomendado para maio?
						</p>
					</div>
					<div className={styles.chatContainer}>
						<div className={styles.inputWrapper}>
							<input
								ref={inputRef}
								type="text"
								className={styles.chatInput}
								placeholder="Pergunte a Flora..."
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								onKeyDown={handleKeyDown}
							/>
							<button
								type="button"
								className={styles.sendButton}
								onClick={handleSend}
								disabled={!message.trim()}
							>
								Enviar{' '}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
